import isEmpty from "lodash/isEmpty";
import {
  CustomFieldsValue,
  Payee,
  PayeeEngagementType,
  PayeeUpdate
} from "@wingspanhq/wingspan-api-sdk";
import { payeeEngagementsApi, payeesApi } from "@api";
import {
  queriesPayeeEngagements,
  queriesPayeeRows,
  queriesPayees
} from "@query";
import { selectorCounterpartyProfileWrite } from "../selectors/selectorCounterpartyProfileWrite";
import { useWSMutation, WSMutationConfig } from "../../../query/helpers";
import { isConflictError } from "../../../v3/utils/apiError";
import { WSServiceError } from "../../../utils/serviceHelper";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";

export type CreatePayeeInput = {
  email: string;
  name?: string;
  company?: string;
  externalId?: string;
  /**
   * Custom-field values, keyed and typed per their definitions. v3 `PayeeCreate`
   * has no home for them, so they are persisted after create via
   * `updatePayeeCustomFields`; skipped when empty.
   */
  customFields?: { [key: string]: CustomFieldsValue };
  /**
   * Payer-owned data (the QBO integration mapping) that v3 `PayeeCreate` has no
   * home for — persisted after create via the per-payee `updatePayee` fields;
   * skipped when empty.
   */
  payerOwnedData?: {
    integration?: {
      quickbooks?: { vendorId?: string; expenseAccountId?: string };
    };
  };
  /**
   * Engagements to assign after create, via v3 `createPayeeEngagement`. Each
   * carries its `engagementType` (from the v3 `Engagement`), which the v3
   * assignment requires and v1 never sent. Assigned sequentially; omit for the
   * bulk flow, which loops engagements itself with per-item progress.
   */
  engagements?: Array<{ id: string; engagementType: PayeeEngagementType }>;
};

/**
 * Tolerates the 409 an already-invited contractor answers. Re-inviting a
 * contractor whose invite is still pending is a 200, and so is re-inviting one
 * whose row was archived and re-added, so no measured path reaches this today.
 * It stays because the failure it prevents is the expensive one: ARC-979 found
 * that letting the invite's error through strands a contractor with no
 * engagements and no retry path from the UI.
 */
const invitePayeeIgnoringAlreadyInvited = async (request: {
  payeeId: string;
  xWingspanAccount?: string;
}): Promise<void> => {
  try {
    await payeesApi.invitePayee(request);
  } catch (error) {
    if (isConflictError(error)) {
      return;
    }
    throw error;
  }
};

/** A contractor reused by email already carries the engagements they were assigned before. */
export const assignPayeeEngagementIgnoringAlreadyAssigned = async (request: {
  payeeId: string;
  engagementId: string;
  engagementType: PayeeEngagementType;
  xWingspanAccount?: string;
}): Promise<void> => {
  try {
    await payeeEngagementsApi.createPayeeEngagement({
      payeeId: request.payeeId,
      payeeEngagementCreate: {
        engagementId: request.engagementId,
        engagementType: request.engagementType
      },
      xWingspanAccount: request.xWingspanAccount
    });
  } catch (error) {
    if (isConflictError(error)) {
      return;
    }
    throw error;
  }
};

/**
 * Creates a contractor (payee) on v3 and issues the Wingspan invite.
 *
 * v1 `createPayee` was one fat call that created the payee, invited them, and
 * persisted payer-owned data (custom fields, QBO mapping) in a single request.
 * v3 splits these: `createPayee` takes the lean payee (email / externalId /
 * profile); the invite (implicit in v1) is an explicit `invitePayee`; custom
 * fields are persisted via the dedicated v3 `updatePayeeCustomFields`; and the
 * QBO vendor / expense-account mapping via the per-payee v3 fields on
 * `updatePayee` (`quickbooksVendorId` / `quickbooksExpenseAccountId`).
 * See V3_MIGRATION_TRACKER.md.
 *
 * **The create upserts on the email, but keeps the stored fields.** An address
 * one of the payer's contractors already holds resolves to that contractor
 * (same id, no second row, no error), which is v1's behavior — but the
 * submitted `profile` and `externalId` are dropped, where v1 applied them. The
 * response is indistinguishable from a fresh create, so the submitted fields
 * are always applied with a follow-up `updatePayee`; on a fresh create that
 * rewrites the values the create just stored. The submitted email is never
 * written back — the record's `profile.email` can be its pending invite's
 * target rather than the value submitted, and writing it would retarget a live
 * invite (ARC-980/ARC-981).
 *
 * Org/expansion context: v1's payer-scoped calls used the `X-Wingspan-User`
 * header; the v3 equivalent is the `xWingspanAccount` request scope (relies on
 * the D1/D24 `userId == accountId` equivalence). In the common (non-org) path
 * `useXWingspanPayerId()` is `undefined`, so no scope override is sent.
 */
export const useCreatePayee = (
  config?: WSMutationConfig<Payee, WSServiceError, CreatePayeeInput>
) => {
  const xWingspanAccount = useXWingspanPayerId();

  return useWSMutation<Payee, WSServiceError, CreatePayeeInput>(
    async input => {
      const profile = selectorCounterpartyProfileWrite({
        name: input.name,
        company: input.company
      });
      const externalId = input.externalId || undefined;

      const payee = await payeesApi.createPayee({
        payeeCreate: { email: input.email, externalId, profile },
        xWingspanAccount
      });

      const payeeId = payee.id!;

      const customFields = input.customFields;
      // QBO vendor / expense-account mapping → per-payee v3 fields.
      const quickbooks = input.payerOwnedData?.integration?.quickbooks;

      const payeeUpdate: PayeeUpdate = {
        // Dropped by the create when it resolves to an existing contractor.
        ...(profile ? { profile } : {}),
        ...(externalId ? { externalId } : {}),
        ...(quickbooks && !isEmpty(quickbooks)
          ? {
              quickbooksVendorId: quickbooks.vendorId,
              quickbooksExpenseAccountId: quickbooks.expenseAccountId
            }
          : {})
      };

      await Promise.all([
        invitePayeeIgnoringAlreadyInvited({ payeeId, xWingspanAccount }),

        customFields && !isEmpty(customFields)
          ? payeesApi.updatePayeeCustomFields({
              payeeId,
              requestBody: customFields,
              xWingspanAccount
            })
          : undefined,

        isEmpty(payeeUpdate)
          ? undefined
          : payeesApi.updatePayee({ payeeId, payeeUpdate, xWingspanAccount }),

        ...(input.engagements ?? []).map(engagement =>
          assignPayeeEngagementIgnoringAlreadyAssigned({
            payeeId,
            engagementId: engagement.id,
            engagementType: engagement.engagementType,
            xWingspanAccount
          })
        )
      ]);

      return payee;
    },
    {
      dependencies: [
        queriesPayees.getPayee.queryKeyBase,
        queriesPayeeEngagements.getPayeeEngagement.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagements.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagementsByPayer.queryKeyBase,
        queriesPayees.listPayees.queryKeyBase,
        queriesPayeeRows.searchPayeeRows.queryKeyBase,
        queriesPayeeEngagements.listPayeeEngagements.queryKeyBase
      ],
      ...config
    }
  );
};
